import React from 'react';
import {Button, Col, Divider, Form, Row, Typography} from 'antd';
import {Formik} from 'formik';
import {
  CheckboxInput,
  CodeMultiFastInput,
  CodeSingleFastInput,
  CodeTreeFastInput,
  DateInput,
  TextAreaInput,
  TextInput,
  UserSelectInput
} from "../Boilerplate/formikComponents";
import {getProjectInvites$} from "../../services/projects";
import {cloneDeep} from "lodash";
import * as Yup from "yup";
import * as R from "ramda";
import {nameSort} from "../../common/sorters";

export const handleSearch = projectId => async (q, id) => {
  let soFar = 0;
  let theSize = 0;
  let nextOffset;
  let data = [];
  do {
    let { response, status } = await getProjectInvites$({
      projectId,
      q, id,
      offset: nextOffset,
    }).toPromise();
    if (status === 204 || response === null) return data;
    const { results, size } = response;
    theSize = size;
    soFar += results.length;
    if (results.length > 0) {
      data = data.concat(results);
      nextOffset = R.last(results).contactuserid;
    }
  } while (soFar < theSize);
  data.sort((a, b) => nameSort(a, b));
  return data;
};

const ProjectForm = props => {
  const { project, onCancel, onSubmit } = props;
  let initials, edit = false;
  if (project) {
    initials = cloneDeep(project);
    edit = true;
  } else {
    initials = {
      status: 'open',
    };
  }
  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 8 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 16 },
    }
  };
  const tailFormItemLayout = {
    wrapperCol: {
      xs: {
        span: 24,
        offset: 0,
      },
      sm: {
        span: 16,
        offset: 8,
      },
    },
  };
  return (
    <Formik
      initialValues={initials}
      onSubmit={(values, { setSubmitting }) => {
        values.contractsize = values.contractsize ? typeof(values.contractsize) == 'string' ? values.contractsize.replace(/[^.0-9+-]/g, '') : values.contractsize : null;
        onSubmit && onSubmit(values);
      }}
      validationSchema={Yup.object().shape({
        id: Yup.string().nullable(),
        number: Yup.string(),
        name: Yup.string().required(),
        status: Yup.string().required().default('open'),
        address: Yup.string().nullable(),
        address2: Yup.string().nullable(),
        city: Yup.string().nullable(),
        state: Yup.string().nullable(),
        zip: Yup.string().nullable(),
        architectname: Yup.string().nullable(),
        engineername: Yup.string().nullable(),
        siteownername: Yup.string().nullable(),
        contractsize: Yup.number().nullable(),
      })}
    >
      {props => {
        const {
          // eslint-disable-next-line
          values, touched, errors, isSubmitting, handleChange, handleBlur, dirty,
          handleSubmit,
          handleReset,
        } = props;
        return (
          <Form>
            <TextInput {...props} formItemLayout={formItemLayout} label='Title' name='name' placeholder='Enter a title'/>
            <TextInput {...props} formItemLayout={formItemLayout} label='Project #' name='number' placeholder='Enter a number'/>
            {!edit && <Row style={{ marginTop: -24, fontSize: 13 }}>
              <Col xs={24} sm={0} style={{ width: 400 }}>
                <Typography><Typography.Paragraph><Typography.Text type={'secondary'}>
                  cProject will auto generate this if left blank. The Project # is intended to be used as a communication
                  reference, written or verbal. [Example: "John, has everyone been added to Project
                  #22?"]</Typography.Text></Typography.Paragraph></Typography>
              </Col>
              <Col xs={0} sm={16} offset={8} style={{ width: 400 }}>
                <Typography><Typography.Paragraph><Typography.Text type={'secondary'}>cProject will auto generate this
                  if left blank. The Project # is intended to be used as a communication reference, written or verbal.
                  [Example: "John, has everyone been added to Project
                  #22?"]</Typography.Text></Typography.Paragraph></Typography>
              </Col>
            </Row>}
            <TextInput {...props} formItemLayout={formItemLayout} label='Estimated Cost' name='contractsize'/>
            <TextAreaInput {...props} formItemLayout={formItemLayout} label='Description' name='description' placeholder='Project description'/>
            {edit && <React.Fragment>
              <UserSelectInput {...props} formItemLayout={formItemLayout} label='Project Lead' name='owneruserid' onSearch={handleSearch(values['id'])}/>
              <UserSelectInput {...props} formItemLayout={formItemLayout} label='Project Creator' name='createdbyuserid' onSearch={handleSearch(values['id'])} disabled={true}/>
            </React.Fragment>}
            <CodeSingleFastInput {...props} disabled={!edit} formItemLayout={formItemLayout} label='Status' name='status' table='projectstatus'/>
            <Divider>Site Address</Divider>
            <CheckboxInput {...props} formItemLayout={formItemLayout} label='Site Opened' name='siteopened'/>
            <TextInput {...props} formItemLayout={formItemLayout} label='Address Line 1' name='address' placeholder='Street address'/>
            <TextInput {...props} formItemLayout={formItemLayout} label='Address Line 2' name='address2' placeholder='Suite #'/>
            <TextInput {...props} formItemLayout={formItemLayout} label='City' name='city' placeholder='City'/>
            <CodeSingleFastInput {...props} formItemLayout={formItemLayout} label='State' name='state' table='states' placeholder='Type a state name...' defaultValue='TX'/>
            <TextInput {...props} formItemLayout={formItemLayout} label='Zip' name='zip' placeholder='Zip Code'/>
            <Divider>Additional Details</Divider>
            <DateInput {...props} formItemLayout={formItemLayout} label='Project Start' name='dateopened'/>
            <DateInput {...props} formItemLayout={formItemLayout} label='Estimated Completion Date' name='estimatedcompletiondate'/>
            <TextAreaInput {...props} formItemLayout={formItemLayout} label='Architect' name='architectname' autosize={{minRows: 1, maxRows: 6}}/>
            <TextAreaInput {...props} formItemLayout={formItemLayout} label='Engineer' name='engineername' autosize={{minRows: 1, maxRows: 6}}/>
            <TextAreaInput {...props} formItemLayout={formItemLayout} label='Site Owner' name='siteownername' autosize={{minRows: 1, maxRows: 6}}/>
            <TextAreaInput {...props} formItemLayout={formItemLayout} label='Customer Name' name='customername' autosize={{minRows: 1, maxRows: 6}}/>
            <Divider/>
            <CodeTreeFastInput {...props} formItemLayout={formItemLayout} label='CSI Codes' name='csicodes' table='csi_hierarchy'/>
            <CodeMultiFastInput {...props} formItemLayout={formItemLayout} label='Industries' name='industries' table='industry'/>
            <CodeMultiFastInput {...props} formItemLayout={formItemLayout} label='Sector' name='sectors' table='sector'/>
            <CodeMultiFastInput {...props} formItemLayout={formItemLayout} label='Set Asides' name='setasides' table='setaside'/>
            <CheckboxInput {...props} formItemLayout={formItemLayout} label='New Construction' name='newconstruction'/>
            <CheckboxInput {...props} formItemLayout={formItemLayout} label='Union' name='unionized'/>
            <CheckboxInput {...props} formItemLayout={formItemLayout} label='Certified Payroll' name='certifiedpayroll'/>
            <CheckboxInput {...props} formItemLayout={formItemLayout} label='Prevailing Wage' name='prevailingwage'/>
            <CheckboxInput {...props} formItemLayout={formItemLayout} label='CCIP Insurance' name='ccipinsurance'/>

            <Form.Item className='submit-controls' {...tailFormItemLayout}>
              <Button.Group compact>
                <Button onClick={handleSubmit} type='primary'>Save</Button>
                <Button onClick={handleReset}>Clear</Button>
                <Button onClick={onCancel}>Cancel</Button>
              </Button.Group>
            </Form.Item>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ProjectForm;


// WEBPACK FOOTER //
// ./src/components/Projects/ProjectForm.js